import type { UninvoicedSummaryClient, UninvoicedMatter, UninvoicedInvoiceScope } from '../../../../AppSettings/services/InvoiceService';

export const CATEGORIES: Record<string, string> = {
  billed_time: 'Time', fixed_fees: 'Fixed fees', expenses: 'Expenses',
  other_billing: 'Other billing', disbursements: 'Disbursements',
};
export type Grouping = 'project' | 'client' | 'leader' | 'area' | 'month' | 'week';
export const GROUPINGS: { value: Grouping; label: string }[] = [
  { value: 'project', label: 'Project #' }, { value: 'client', label: 'Client' },
  { value: 'leader', label: 'Leader' }, { value: 'area', label: 'Area' },
  { value: 'month', label: 'Month' }, { value: 'week', label: 'Week' },
];
export type Project = UninvoicedMatter & { clientid: string; client_name: string };
export type ProjectFilters = { search: string; client: string; leader: string; area: string; status: string; project: string; min: string; max: string };
export const EMPTY_FILTERS: ProjectFilters = { search: '', client: '', leader: '', area: '', status: '', project: '', min: '', max: '' };
export const dayString = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
export const displayDate = (day?: string | null) => day ? new Date(`${day.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Undated';
export const flattenProjects = (clients: UninvoicedSummaryClient[]): Project[] => clients.flatMap(c => c.matters.map(m => ({
  ...m, clientid: c.clientid, client_name: c.client_name,
  project_number: m.project_number || m.matterid, leader_name: m.leader_name || 'Unassigned',
  leader_id: m.leader_id || '', area: m.area || 'Unassigned', status: m.status || 'Unspecified',
})));

export function filterProjects(projects: Project[], filters: ProjectFilters): Project[] {
  const search = filters.search.trim().toLowerCase();
  return projects.filter(p => (!search || [p.project_number, p.matter_name, p.client_name, p.leader_name, p.area].some(v => v?.toLowerCase().includes(search)))
    && (!filters.project || p.matterid === filters.project)
    && (!filters.client || p.clientid === filters.client)
    && (!filters.leader || (p.leader_id || '__unassigned') === filters.leader)
    && (!filters.area || p.area === filters.area)
    && (!filters.status || p.status === filters.status)
    && (filters.min === '' || p.uninvoiced_total >= Number(filters.min))
    && (filters.max === '' || p.uninvoiced_total <= Number(filters.max)));
}

export interface ProjectGroup { key: string; label: string; projects: Project[]; start?: string; end?: string; undatedOnly?: boolean }
export function groupProjects(projects: Project[], grouping: Grouping): ProjectGroup[] {
  if (grouping === 'project') return [{ key: 'all', label: 'All projects', projects }];
  const groups = new Map<string, ProjectGroup>();
  const add = (key: string, label: string, project: Project, period?: Partial<ProjectGroup>) => {
    if (!groups.has(key)) groups.set(key, { key, label, projects: [], ...period });
    groups.get(key)!.projects.push(project);
  };
  for (const project of projects) {
    if (grouping === 'client') add(project.clientid, project.client_name, project);
    else if (grouping === 'leader') add(project.leader_id || '__unassigned', project.leader_name!, project);
    else if (grouping === 'area') add(project.area!, project.area!, project);
    else {
      const slices = new Map<string, { project: Project; label: string; start?: string; end?: string; undatedOnly?: boolean }>();
      for (const period of project.periods || []) {
        let key = 'undated', label = 'Undated items', start: string | undefined, end: string | undefined;
        if (period.date) {
          const date = new Date(`${period.date}T12:00:00`);
          if (grouping === 'month') {
            start = dayString(new Date(date.getFullYear(), date.getMonth(), 1));
            end = dayString(new Date(date.getFullYear(), date.getMonth() + 1, 0));
            label = date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
          } else {
            date.setDate(date.getDate() - (date.getDay() + 6) % 7);
            start = dayString(date);
            date.setDate(date.getDate() + 6);
            end = dayString(date);
            label = `Week of ${displayDate(start)}`;
          }
          key = start;
        }
        if (!slices.has(key)) slices.set(key, { label, start, end, undatedOnly: !period.date, project: { ...project, uninvoiced_count: 0, uninvoiced_total: 0, oldest_date: null, breakdown: {}, periods: [] } });
        const p = slices.get(key)!.project;
        p.uninvoiced_count += period.count;
        p.uninvoiced_total += period.total;
        if (period.date && (!p.oldest_date || period.date < p.oldest_date)) p.oldest_date = period.date;
        const breakdown = p.breakdown[period.source_type] || { count: 0, total: 0 };
        p.breakdown[period.source_type] = { count: breakdown.count + period.count, total: breakdown.total + period.total };
        p.periods!.push(period);
      }
      slices.forEach(({ project: p, label, ...period }, key) => add(key, label, p, period));
    }
  }
  return [...groups.values()].sort((a, b) => {
    if (grouping === 'month' || grouping === 'week') return a.key === 'undated' ? 1 : b.key === 'undated' ? -1 : b.key.localeCompare(a.key);
    return a.label.localeCompare(b.label, undefined, { numeric: true });
  });
}

export function groupScope(scope: UninvoicedInvoiceScope, group: ProjectGroup): UninvoicedInvoiceScope {
  return { ...scope,
    ...(group.start ? { start_date: scope.start_date && scope.start_date > group.start ? scope.start_date : group.start,
      end_date: scope.end_date && scope.end_date < group.end! ? scope.end_date : group.end, include_undated: false } : {}),
    ...(group.undatedOnly ? { undatedOnly: true, include_undated: true } : {}),
  };
}

/** Apply the workspace scope to the wizard's native source records, before import selection. */
export function matchesInvoiceScope(record: Record<string, any>, category: string, scope?: UninvoicedInvoiceScope): boolean {
  if (!scope) return true;
  if (!scope.matterIds.includes(String(record.matterid ?? ''))) return false;
  if (scope.category && scope.category !== category) return false;
  const day = String(record.date_recorded || record.date_added || record.date || record.expense_date || record.created_at || '').slice(0, 10);
  if (scope.undatedOnly) return !day;
  if (!day) return scope.include_undated !== false;
  return (!scope.start_date || day >= scope.start_date) && (!scope.end_date || day <= scope.end_date);
}
